/**
 * ActiveCallBanner (Phase 6 item #54).
 *
 * In-flow banner at the top of the workspace column whenever there's an
 * active browser call. It sits ABOVE the TopRibbon in normal flow (not a
 * fixed overlay) so it pushes the ribbon + content down instead of covering
 * them, and persists across route changes (rendered inside AppShell). Hangup
 * button ends the call; the elapsed timer counts up from startedAt.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, Loader2, Mic, MicOff } from 'lucide-react';
import { useBrowserCall } from '../../providers/BrowserCallProvider.js';
import { cn } from '../../lib/utils.js';

function fmtElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function ActiveCallBanner() {
  const { state, hangup, muted, mute } = useBrowserCall();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (state.status !== 'on-call') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [state.status]);

  if (state.status === 'idle' || state.status === 'ready') return null;

  return (
    <AnimatePresence>
      <motion.div
        key={state.status}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.18 }}
        role="status"
        aria-live="polite"
        className="relative z-30 flex w-full flex-shrink-0 items-center justify-between gap-3 px-4 sm:px-6 py-2 bg-success/10 dark:bg-success/20 border-b border-success/30 text-success-fg dark:text-emerald-100"
      >
        <div className="flex items-center gap-3 min-w-0">
          {state.status === 'dialing' ? (
            <Loader2 size={14} className="animate-spin flex-shrink-0" />
          ) : state.status === 'on-call' ? (
            <Phone size={14} className="flex-shrink-0" />
          ) : (
            <Phone size={14} className="flex-shrink-0 opacity-60" />
          )}
          <span className="text-sm font-medium truncate">
            {state.status === 'dialing' && `Dialing ${state.toNumber}...`}
            {state.status === 'on-call' && `On call with ${state.toNumber}`}
            {state.status === 'initializing' && 'Initializing call device...'}
            {state.status === 'error' && state.reason}
          </span>
          {state.status === 'on-call' && (
            <span className="text-xs font-mono text-success ml-1">
              {fmtElapsed(now - state.startedAt)}
            </span>
          )}
        </div>
        {(state.status === 'dialing' || state.status === 'on-call') && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {state.status === 'on-call' && (
              <button
                type="button"
                onClick={() => mute()}
                aria-pressed={muted}
                aria-label={muted ? 'Unmute microphone' : 'Mute microphone'}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-colors',
                  muted
                    ? 'bg-amber-500 text-white hover:opacity-90'
                    : 'bg-success/20 text-success dark:text-emerald-100 hover:bg-success/30',
                )}
              >
                {muted ? <MicOff size={11} /> : <Mic size={11} />}
                {muted ? 'Muted' : 'Mute'}
              </button>
            )}
            <button
              type="button"
              onClick={hangup}
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-danger text-danger-fg dark:bg-red-700 dark:text-red-50 text-xs font-medium hover:opacity-90 transition-opacity"
              aria-label="End call"
            >
              <PhoneOff size={11} />
              Hang up
            </button>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
